/* eslint-disable prettier/prettier */
import { useState, useCallback, useEffect } from 'react';
import { preferenceService } from '../services/preference.service';
import { UserPreferences, UpdatePreferencesRequest } from '../types/preference';

export const usePreference = () => {
	const [preferences, setPreferences] = useState<UserPreferences | null>(
		null
	);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	/**
	 * Fetch user preferences
	 */
	const fetchUserPreferences = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await preferenceService.getUserPreferences();
			if (response.success && response.data) {
				setPreferences(response.data);
			} else {
				setError(response.message || 'Failed to fetch preferences');
			}
		} catch (err: any) {
			setError(err.message || 'An unexpected error occurred');
		} finally {
			setLoading(false);
		}
	}, []);

	/**
	 * Update Preferences (Mirrors your updateProfile logic)
	 */
	const updatePreferences = async (data: UpdatePreferencesRequest) => {
		setLoading(true);
		setError(null);
		setSuccess(false);

		try {
			const response = await preferenceService.updatePreferences(data);
			if (response.success) {
				setSuccess(true);
				// Refresh the data after successful update
				await fetchUserPreferences();
			} else {
				setError(response.message || 'Failed to update preferences');
			}
		} catch (err: any) {
			setError(err.message || 'Failed to update preferences');
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Reset Preferences
	 */
	const resetPreferences = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await preferenceService.resetPreferences();
			if (response.success) {
				await fetchUserPreferences();
			} else {
				setError(response.message || 'Failed to reset preferences');
			}
		} catch (err: any) {
			setError(err.message || 'Failed to reset preferences');
		} finally {
			setLoading(false);
		}
	};

	// Auto-fetch on mount
	useEffect(() => {
		fetchUserPreferences();
	}, [fetchUserPreferences]);

	return {
		preferences,
		loading,
		error,
		success,
		updatePreferences,
		resetPreferences,
		refresh: fetchUserPreferences,
		setSuccess,
	};
};
